import {hasFeatureFlag} from '../../util/FeatureUtil';
import {hasPermission, hasTaskUpdatePermission, isClientUser} from '../../util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../../Permissions';
import {
	getNotifications as getNovaNotifications,
	subscribeToNotifications as subscribeToNovaNotifications,
} from './novaNotificationStore';
import type {NovaNotificationEntry} from './novaNotificationStore';

export type TaggedNovaNotificationEntry = NovaNotificationEntry & {isNovaNotification: true};

function tagAsNova(entries: NovaNotificationEntry[]): TaggedNovaNotificationEntry[] {
	return entries.map(entry => ({...entry, isNovaNotification: true}));
}

// Both merge surfaces (the header popover and the fullscreen /my-profile/all-notifications page)
// need the same tagged snapshot for their initial state — bundled here so neither can drift from
// the other by, say, tagging on every render instead of once per change.
// A Nova notification tells a person to go and act on an answer. Somebody who may not act on the
// task cannot act on the answer either, so the notification would send them to a thread they can
// only read — and the badge would put a number on it. They can still open the task and read the
// conversation; what stops is being chased about it.
//
// The permission and the client restriction are the same two checks the invocation control applies,
// for the same reason: either one on its own means this person cannot act.
function mayBeNotified(): boolean {
	const clientRestricted = isClientUser() && hasPermission(PERMISSION_TYPE.CLIENT_RESTRICTED_TASK_ACTIONS);
	return hasFeatureFlag('nova_agent_invocation') && hasTaskUpdatePermission() && !clientRestricted;
}

export function initialNovaNotifications(): TaggedNovaNotificationEntry[] {
	return mayBeNotified() ? tagAsNova(getNovaNotifications()) : [];
}

// Returns undefined when the flag is off, matching the caller's own "only unsubscribe if we ever
// subscribed" cleanup pattern (`if (this.novaUnsubscribe) this.novaUnsubscribe()`).
export function subscribeToMergedNovaNotifications(
	onChange: (entries: TaggedNovaNotificationEntry[]) => void
): (() => void) | undefined {
	if (!mayBeNotified()) return undefined;
	return subscribeToNovaNotifications(() => onChange(tagAsNova(getNovaNotifications())));
}

// A nova entry counts toward the header badge only if it arrived after the last time the
// notification menu was opened - opening clears the badge contribution without removing the
// entries themselves from the list, matching the native/ticket badges' own "opening clears it"
// behavior (nova's separate per-row dismiss is still what actually removes an entry). An
// overflow_summary entry stands in for several finished runs folded into one row once the display
// cap is hit, so it contributes its own count, not 1 - counting it as 1 would undercount the badge
// past the cap.
export function countUnseenNovaNotifications(entries: NovaNotificationEntry[], seenAt: number): number {
	return entries
		.filter(entry => entry.createdAt > seenAt)
		.reduce((total, entry) => total + (entry.kind === 'overflow_summary' ? entry.count : 1), 0);
}
